import React, { useState, useEffect } from 'react';
import { Package, Download, Play, Eye, FileText, Music, Lock, Unlock, ExternalLink } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../utils/imageUrl';

const FILE_TYPE_ICONS = {
  image: Eye,
  mp4: Play,
  mp3: Music,
  pdf: FileText,
  doc: FileText,
  file: Download
};

const UnlockedProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activationStatus, setActivationStatus] = useState(null);

  useEffect(() => {
    fetchUnlockedProducts();
  }, []);

  const fetchUnlockedProducts = async () => {
    try {
      const [productsRes, statusRes] = await Promise.all([
        api.get('/activation/unlocked-products'),
        api.get('/activation/status')
      ]);
      setProducts(productsRes.data.products || []);
      setActivationStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to fetch unlocked products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (file) => {
    window.open(file.url, '_blank');
    toast.success(`Downloading ${file.name}`);
  };

  const getFileIcon = (fileType) => {
    const Icon = FILE_TYPE_ICONS[fileType] || FILE_TYPE_ICONS.file;
    return Icon;
  };

  const renderFilePreview = (file) => {
    const fileType = file.file_type || 'file';
    
    if (fileType === 'image' || file.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return (
        <div className="aspect-video bg-black/50 rounded-lg overflow-hidden">
          <img src={getImageUrl(file.url)} alt={file.name} className="w-full h-full object-contain" />
        </div>
      );
    }
    
    if (fileType === 'mp4' || file.url?.match(/\.(mp4|webm|mov)$/i)) {
      return (
        <div className="aspect-video bg-black rounded-lg overflow-hidden">
          <video 
            src={getImageUrl(file.url)} 
            controls 
            className="w-full h-full"
            controlsList="nodownload"
          >
            Your browser does not support video playback.
          </video>
        </div>
      );
    }
    
    if (fileType === 'mp3' || file.url?.match(/\.(mp3|wav|ogg|aac)$/i)) {
      return (
        <div className="bg-white/5 rounded-lg p-4">
          <audio src={getImageUrl(file.url)} controls className="w-full">
            Your browser does not support audio playback.
          </audio>
        </div>
      );
    }
    
    if (fileType === 'pdf' || file.url?.match(/\.pdf$/i)) {
      return (
        <div className="aspect-video bg-white/5 rounded-lg overflow-hidden">
          <iframe 
            src={getImageUrl(file.url)} 
            title={file.name}
            className="w-full h-full"
          />
        </div>
      );
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="glass-card p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
          <div className="h-24 bg-white/10 rounded"></div>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="glass-card p-6 border-2 border-dashed border-white/20" data-testid="unlocked-products-empty">
        <div className="text-center py-6">
          <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Unlocked Products</h3>
          <p className="text-muted-foreground text-sm mb-4">
            {activationStatus?.is_activated 
              ? "You haven't unlocked any products yet. Contact admin for access."
              : "Activate your account to unlock products!"
            }
          </p>
          {!activationStatus?.is_activated && (
            <a 
              href="/activation" 
              className="btn-primary inline-flex items-center gap-2"
            >
              <Unlock className="w-4 h-4" />
              Activate Now
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="unlocked-products-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-500/20 rounded-lg">
            <Unlock className="w-6 h-6 text-green-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold">My Unlocked Products</h2>
            <p className="text-sm text-muted-foreground">{products.length} product{products.length !== 1 ? 's' : ''} unlocked</p>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <div 
            key={product.id} 
            className="glass-card overflow-hidden group hover:border-green-500/50 transition-all"
            data-testid={`unlocked-product-${product.id}`}
          >
            {/* Product Image */}
            {product.images?.[0] && (
              <div className="aspect-video bg-black/30 relative">
                <img 
                  src={getImageUrl(product.images[0])} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  <span className="px-2 py-1 bg-green-500/90 text-white text-xs rounded-full flex items-center gap-1">
                    <Unlock className="w-3 h-3" />
                    Unlocked
                  </span>
                </div>
              </div>
            )}

            <div className="p-4">
              <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{product.description}</p>
              
              {/* Files List */}
              {product.files && product.files.length > 0 && (
                <div className="space-y-2 mb-4">
                  <p className="text-xs text-muted-foreground font-medium">Available Files ({product.files.length})</p>
                  {product.files.slice(0, 3).map((file, idx) => {
                    const Icon = getFileIcon(file.file_type);
                    return (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 text-sm bg-white/5 p-2 rounded-lg hover:bg-white/10 transition-colors"
                      >
                        <Icon className="w-4 h-4 text-primary" />
                        <span className="flex-1 truncate">{file.name}</span>
                        <span className="text-xs text-muted-foreground uppercase">{file.file_type}</span>
                      </div>
                    );
                  })}
                  {product.files.length > 3 && (
                    <p className="text-xs text-muted-foreground">+{product.files.length - 3} more files</p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedProduct(product)}
                  className="flex-1 btn-primary text-sm py-2"
                  data-testid={`view-product-${product.id}`}
                >
                  <Eye className="w-4 h-4 inline mr-1" />
                  View & Download
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-background/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card max-w-4xl w-full max-h-[90vh] overflow-y-auto" data-testid="product-detail-modal">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold">{selectedProduct.name}</h2>
                  <p className="text-muted-foreground">{selectedProduct.description}</p>
                </div>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  ✕
                </button>
              </div>

              {/* Files Grid */}
              {selectedProduct.files && selectedProduct.files.length > 0 ? (
                <div className="space-y-6">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" />
                    Product Files ({selectedProduct.files.length})
                  </h3>
                  
                  {selectedProduct.files.map((file, idx) => {
                    const Icon = getFileIcon(file.file_type);
                    return (
                      <div key={idx} className="bg-white/5 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/20 rounded-lg">
                              <Icon className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{file.name}</p>
                              <p className="text-xs text-muted-foreground uppercase">
                                {file.file_type} {file.size_mb && `• ${file.size_mb} MB`}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownload(file)}
                            className="px-4 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30 transition-colors flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                        
                        {/* File Preview */}
                        {renderFilePreview(file)}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No files available for this product</p>
                </div>
              )}

              {/* External Links */}
              {selectedProduct.external_links && selectedProduct.external_links.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <ExternalLink className="w-5 h-5 text-primary" />
                    External Links
                  </h3>
                  <div className="space-y-2">
                    {selectedProduct.external_links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 text-primary" />
                        <span>{link.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnlockedProducts;
